import { Link } from "react-router-dom";
import BlogPostLayout from "./BlogPostLayout";
import { getPostBySlug } from "../../lib/blog";

const FAQ = [
  {
    q: "Un nid de frelons asiatiques vide en hiver est-il dangereux ?",
    a: "En plein hiver, le nid est en général abandonné : la colonie meurt et les futures reines hivernent ailleurs. Le danger est donc faible. En début d'hiver ou en cas de douceur, quelques ouvrières peuvent encore être présentes : observez le nid quelques minutes avant de vous approcher.",
  },
  {
    q: "Les frelons reviendront-ils dans le même nid au printemps ?",
    a: "Non. Un nid de frelons asiatiques ne sert qu'une saison. Au printemps, chaque reine fondatrice construit un nouveau nid, souvent à un autre endroit.",
  },
  {
    q: "Faut-il faire détruire un nid vide ?",
    a: "Ce n'est généralement pas nécessaire. Le nid se dégrade avec les intempéries. On peut le retirer s'il gêne ou s'il est accessible sans risque, mais un traitement insecticide sur un nid vide n'a aucun intérêt.",
  },
  {
    q: "Comment savoir si un nid est encore actif ?",
    a: "Observez l'orifice de sortie pendant quelques minutes, par temps doux, à bonne distance. S'il y a des allées et venues, le nid est actif : ne vous approchez pas et faites appel à un professionnel.",
  },
];

export default function NidFrelonsVideHiver() {
  const post = getPostBySlug("nid-frelons-asiatiques-vide-hiver-que-faire");
  return (
    <BlogPostLayout post={post} faq={FAQ}>
      <p className="article-lead">
        Chaque automne, c'est la même surprise : les feuilles tombent et révèlent, tout en haut d'un chêne ou d'un
        peuplier, une grosse boule de papier brun. Un nid de frelons asiatiques passé inaperçu tout l'été. Faut-il
        s'inquiéter ? Faut-il le faire détruire ? Voici ce qu'il faut savoir.
      </p>

      <h2>Le cycle de vie d'une colonie de frelons asiatiques</h2>
      <h3>Du printemps à l'automne</h3>
      <p>
        Au printemps, une reine fondatrice sort d'hibernation et construit seule un petit nid primaire, souvent dans un
        abri, sous un avant-toit ou dans une haie. La colonie grandit, puis déménage fréquemment en été vers un nid
        secondaire beaucoup plus gros, généralement placé haut dans un arbre. À la fin de l'été et en automne, la
        colonie atteint son maximum et produit les futures reines.
      </p>
      <h3>En hiver</h3>
      <p>
        Les futures reines, fécondées, quittent le nid pour hiverner à l'abri : sous une écorce, dans le sol, dans une
        anfractuosité. Les ouvrières et l'ancienne reine meurent avec le froid. Le nid ne sera <strong>jamais
        réutilisé</strong> l'année suivante.
      </p>
      <p>
        Pour bien différencier ces nids de ceux des guêpes ou du frelon européen, voir notre guide{" "}
        <Link to="/conseils/reconnaitre-nid-frelons-asiatiques-creuse">reconnaître un nid de frelons asiatiques</Link>.
      </p>

      <h2>Le nid est-il encore dangereux ?</h2>
      <p>
        En plein hiver, le risque est faible. Mais attention aux idées reçues : en novembre ou lors d'un début d'hiver
        doux, il peut encore rester des ouvrières actives, capables de défendre le nid. Avant toute chose, observez-le à
        distance, par une journée douce, pendant quelques minutes. S'il n'y a aucune entrée ni sortie, il est très
        probablement abandonné.
      </p>

      <h2>Faut-il retirer ou détruire un nid vide ?</h2>
      <h3>Le plus souvent, non</h3>
      <p>
        Un nid vide n'abrite plus de colonie et ne sera pas recolonisé. Exposé à la pluie et au vent, il se désagrège
        de lui-même au fil des semaines. Un traitement insecticide n'a aucun intérêt sur un nid abandonné : il disperse
        des produits inutilement dans l'environnement.
      </p>
      <h3>Quand le retirer quand même</h3>
      <p>
        S'il est bas, accessible et qu'il vous gêne (au-dessus d'une terrasse, dans un abri de jardin), vous pouvez le
        décrocher après avoir vérifié qu'il est inactif, avec des gants. Pour un nid en hauteur, ne prenez aucun risque
        de chute : l'intervention relève alors de l'élagage. Voir notre page{" "}
        <Link to="/elagage-creuse">élagage en Creuse</Link>.
      </p>

      <h2>Ne pas confondre avec d'autres nids</h2>
      <p>
        Tous les nids visibles en hiver ne sont pas des nids de frelons asiatiques. Le <strong>frelon européen</strong>{" "}
        s'installe plutôt dans des cavités : arbre creux, grenier, conduit de cheminée, coffre de volet. Les{" "}
        <strong>guêpes</strong> construisent des nids plus petits, souvent dans le sol, sous une toiture ou dans un abri.
        Une boule de feuilles et de brindilles en haut d'un arbre est souvent un nid d'écureuil, et les masses blanches
        et soyeuses sur les pins sont des nids de chenilles processionnaires, eux bien actifs en hiver. Dans le doute,
        une photo envoyée par WhatsApp nous permet généralement de vous répondre rapidement.
      </p>

      <h2>Que faire pour l'année prochaine ?</h2>
      <h3>Noter l'emplacement</h3>
      <p>
        Un nid repéré cet hiver indique que votre secteur est fréquenté. Notez où il se trouvait et surveillez les
        alentours au printemps, quand les reines construisent les nids primaires, plus petits et plus faciles à traiter.
      </p>
      <h3>Signaler le nid</h3>
      <p>
        Dans de nombreuses régions, des plateformes de signalement recensent les nids pour suivre la progression de
        l'espèce. Un signalement, même pour un nid vide, est utile.
      </p>
      <h3>Agir vite sur les nids actifs</h3>
      <p>
        Au printemps et en été, un nid actif près d'une habitation se traite sans attendre, par un professionnel
        équipé. C'est notre service de{" "}
        <Link to="/destruction-frelons-creuse">destruction de nids de frelons en Creuse</Link>. Pour les bons réflexes en
        cas de découverte, lisez aussi{" "}
        <Link to="/conseils/frelons-asiatiques-creuse-que-faire">frelons asiatiques en Creuse : que faire ?</Link>.
      </p>
    </BlogPostLayout>
  );
}
